import { Button, Card } from "@justworkshr/milo-core";
import {
  ActionFooter,
  FormField,
  Form as MiloForm,
  RadioButton,
  RadioButtonGroup,
  Select,
  SelectOption,
  SingleDatePicker,
  TextInput,
} from "@justworkshr/milo-form";
import { Formik, type FormikConfig } from "formik";
import clsx from "lib/styling-utils/clsx";
import { useContext, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import { SupportedCountriesContext } from "../../contexts/supportedCountriesContext";
import DepartmentSelectField from "../components/DepartmentSelectField";
import ManagerSelectField from "../components/ManagerSelectField";
import { ContractTerm, EmailTypes, PayBasis } from "../types";
import styles from "./Form.module.css";
import MX_CONFIG, { type SchemaType as MXSchema } from "./mx";
import NL_CONFIG, { type SchemaType as NLSchema } from "./nl";
import IN_CONFIG, { type SchemaType as INSchema } from "./in";
import GB_CONFIG, { type SchemaType as GBSchema } from "./gb";
import CA_CONFIG, { type SchemaType as CASchema } from "./ca";
import FR_CONFIG, { type SchemaType as FRSchema } from "./fr";
import DE_CONFIG, { type SchemaType as DESchema } from "./de";
import PT_CONFIG, { type SchemaType as PTSchema } from "./pt";
import IE_CONFIG, { type SchemaType as IESchema } from "./ie";
import ES_CONFIG, { type SchemaType as ESSchema } from "./es";
import baseSchema from "./schema";
import { addYears, formatToISODate } from "pages/employer-of-record/utils";
import { useGetFeatureFlag } from "lib/launch-darkly";
import WorkCountrySelectField from "../components/WorkCountrySelectField/WorkCountrySelectField";

export const COUNTRY_EXTENDED_CONFIG = {
  MX: MX_CONFIG,
  NL: NL_CONFIG,
  IN: IN_CONFIG,
  GB: GB_CONFIG,
  CA: CA_CONFIG,
  FR: FR_CONFIG,
  DE: DE_CONFIG,
  PT: PT_CONFIG,
  IE: IE_CONFIG,
  ES: ES_CONFIG,
} as const;

export const isCountryExtendedConfig = (
  key: string
): key is keyof typeof COUNTRY_EXTENDED_CONFIG =>
  key in COUNTRY_EXTENDED_CONFIG;

type BaseSchemaType = Yup.InferType<typeof baseSchema>;
export type SchemaType =
  | BaseSchemaType
  | (BaseSchemaType & MXSchema)
  | (BaseSchemaType & NLSchema)
  | (BaseSchemaType & INSchema)
  | (BaseSchemaType & GBSchema)
  | (BaseSchemaType & CASchema)
  | (BaseSchemaType & FRSchema)
  | (BaseSchemaType & DESchema)
  | (BaseSchemaType & PTSchema)
  | (BaseSchemaType & IESchema)
  | (BaseSchemaType & ESSchema);

export type InviteFormProps = {
  initialValues: SchemaType;
  isLoading?: boolean;
  onFormSubmit: FormikConfig<SchemaType>["onSubmit"];
};

const minDate = new Date();
const maxDate = addYears(minDate, 1);
const farFutureDate = addYears(minDate, 100);

export default function Form({
  initialValues,
  isLoading,
  onFormSubmit,
}: InviteFormProps) {
  const { t } = useTranslation();
  const { getFeatureFlag } = useGetFeatureFlag();

  const { supportedCountriesData, getCurrency } = useContext(
    SupportedCountriesContext
  );
  const updatedSchema = baseSchema.shape({
    workCountry: Yup.string()
      .oneOf(["", ...Object.keys(supportedCountriesData)])
      .required("Required"),
    payRate: Yup.number()
      .typeError("Must be a number.")
      .required("Required")
      .test("is-greater-than-1", `Must be $1 or greater.`, function (number) {
        const valid = !isNaN(number) && number >= 1;
        const country = this.resolve(Yup.ref("workCountry")) as string;
        const symbol = getCurrency(country).symbol;
        if (!valid && symbol) {
          return this.createError({
            message: `Must be ${symbol}1 or greater.`,
          });
        }
        return valid;
      }),
  });

  const generateLazyValidationSchema = () =>
    Yup.lazy((values: SchemaType) => {
      const workCountry = values.workCountry;

      // For now, this is the only way to achieve type narrowing to play nicely with concat
      if (isCountryExtendedConfig(workCountry)) {
        if (values.workCountry === "MX" && COUNTRY_EXTENDED_CONFIG["MX"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["MX"].schema);
        }
        if (values.workCountry === "NL" && COUNTRY_EXTENDED_CONFIG["NL"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["NL"].schema);
        }
        if (values.workCountry === "IN" && COUNTRY_EXTENDED_CONFIG["IN"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["IN"].schema);
        }
        if (values.workCountry === "GB" && COUNTRY_EXTENDED_CONFIG["GB"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["GB"].schema);
        }
        if (values.workCountry === "CA" && COUNTRY_EXTENDED_CONFIG["CA"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["CA"].schema);
        }
        if (values.workCountry === "FR" && COUNTRY_EXTENDED_CONFIG["FR"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["FR"].schema);
        }
        if (values.workCountry === "DE" && COUNTRY_EXTENDED_CONFIG["DE"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["DE"].schema);
        }
        if (values.workCountry === "PT" && COUNTRY_EXTENDED_CONFIG["PT"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["PT"].schema);
        }
        if (values.workCountry === "IE" && COUNTRY_EXTENDED_CONFIG["IE"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["IE"].schema);
        }
        if (values.workCountry === "ES" && COUNTRY_EXTENDED_CONFIG["ES"]) {
          return updatedSchema.concat(COUNTRY_EXTENDED_CONFIG["ES"].schema);
        }
      }

      return updatedSchema;
    });

  return (
    <Formik<SchemaType>
      initialValues={initialValues}
      onSubmit={onFormSubmit}
      validationSchema={generateLazyValidationSchema}
      enableReinitialize
    >
      {({
        handleBlur,
        handleChange,
        handleSubmit,
        setFieldValue,
        setValues,
        touched,
        errors,
        values,
      }) => {
        const fieldErrorHandler = (name: keyof BaseSchemaType) =>
          errors[name] && touched[name] ? errors[name] : undefined;

        const handleHomeEmailFieldChange = (
          event: ChangeEvent<HTMLInputElement>
        ) => {
          if (!event.target.value) {
            setFieldValue("sendInvitationTo", EmailTypes.Work);
          }

          handleChange(event);
        };

        const payAmountLabel = () => {
          const currency = getCurrency(values.workCountry).currencyCode;
          const formattedCurrency = currency ? ` (${currency})` : "";
          const basis =
            values.payBasis === PayBasis.Hourly
              ? t(`Pay amount per hour`)
              : t(`Pay amount per year`);
          return basis + formattedCurrency;
        };

        const startDatePickerHandlePick = (date: Date) => {
          setFieldValue("startDate", date ? formatToISODate(date) : "");
        };

        const honoredStartDatePickerHandlePick = (date: Date) => {
          setFieldValue("honoredStartDate", date ? formatToISODate(date) : "");
        };

        const handleWorkCountryOnChange = (
          event: ChangeEvent<HTMLSelectElement>
        ) => {
          const baseInitialValues = () => {
            const filteredEntries = Object.entries(values).filter(
              ([key]) => key in initialValues
            );
            const filteredValues = Object.fromEntries(filteredEntries);
            return {
              ...initialValues,
              ...filteredValues,
              // Temporary fix to ensure contract term is indefinite for all countries, until we enable both indefinite and fixed terms
              contractTerm: ContractTerm.Indefinite,
            };
          };

          const workCountry = event.target.value;
          const baseValues = baseInitialValues();

          if (isCountryExtendedConfig(workCountry)) {
            setValues(
              {
                ...baseValues,
                ...COUNTRY_EXTENDED_CONFIG[workCountry]?.initialValues,
              },
              false
            );
          } else {
            setValues(baseValues, false);
          }

          return handleChange(event);
        };

        return (
          <MiloForm onSubmit={handleSubmit} noValidate>
            <div className={styles.formBody}>
              <div>
                <Card title={t("Employee information")}>
                  <div className={styles.cardBody}>
                    <FormField
                      required
                      name="firstName"
                      label={t("First name")}
                      message={
                        values.workCountry === "GR"
                          ? "Use Greek letters (e.g. Νικη). If a Greek name is not available, use Latin letters (e.g. Nick)."
                          : undefined
                      }
                      error={fieldErrorHandler("firstName")}
                    >
                      <TextInput
                        required
                        name="firstName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.firstName}
                      />
                    </FormField>
                    <FormField
                      required
                      name="lastName"
                      label={t("Last name")}
                      message={
                        values.workCountry === "GR"
                          ? "Use Greek letters (e.g. Ρίζος). If a Greek name is not available, use Latin letters (e.g. Smith)"
                          : undefined
                      }
                      error={fieldErrorHandler("lastName")}
                    >
                      <TextInput
                        required
                        name="lastName"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.lastName}
                      />
                    </FormField>
                  </div>
                </Card>
              </div>
              <div>
                <Card title={t("Contact information")}>
                  <div className={styles.cardBody}>
                    <FormField
                      name="homeEmail"
                      label={t("Home email address")}
                      error={fieldErrorHandler("homeEmail")}
                    >
                      <TextInput
                        name="homeEmail"
                        onChange={handleHomeEmailFieldChange}
                        onBlur={handleBlur}
                        value={values.homeEmail}
                      />
                    </FormField>
                    <FormField
                      name="workEmail"
                      label={t("Work email address")}
                      required
                      error={fieldErrorHandler("workEmail")}
                    >
                      <TextInput
                        required
                        name="workEmail"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.workEmail}
                      />
                    </FormField>
                  </div>
                </Card>
              </div>
              <div>
                <Card title={t("Job & compensation information")}>
                  <div className={styles.cardBody}>
                    <WorkCountrySelectField
                      value={values.workCountry}
                      error={fieldErrorHandler("workCountry")}
                      handleChange={handleWorkCountryOnChange}
                      handleBlur={handleBlur}
                    />
                    <FormField
                      required
                      name="contractTerm"
                      label={t("Contract term")}
                      error={fieldErrorHandler("contractTerm")}
                    >
                      <Select
                        required
                        name="contractTerm"
                        placeholder={t("Select contract type")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.contractTerm}
                        disabled
                      >
                        <SelectOption
                          value={ContractTerm.Indefinite}
                          key={ContractTerm.Indefinite}
                        >
                          {t("Indefinite")}
                        </SelectOption>
                        <SelectOption
                          value={ContractTerm.Fixed}
                          key={ContractTerm.Fixed}
                        >
                          {t("Fixed")}
                        </SelectOption>
                      </Select>
                    </FormField>
                    <ManagerSelectField
                      name="managerUuid"
                      error={fieldErrorHandler("managerUuid")}
                      value={values.managerUuid}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />
                    <FormField
                      required
                      name="jobTitle"
                      label={t("Job title")}
                      error={fieldErrorHandler("jobTitle")}
                    >
                      <TextInput
                        required
                        name="jobTitle"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.jobTitle}
                      />
                    </FormField>
                    <FormField
                      required
                      name="roleResponsibilities"
                      label={t("Role Responsibilities")}
                      error={fieldErrorHandler("roleResponsibilities")}
                    >
                      <textarea
                        className={clsx(
                          styles.textArea,
                          errors.roleResponsibilities &&
                            touched.roleResponsibilities &&
                            styles.error
                        )}
                        name="roleResponsibilities"
                        required
                        value={values.roleResponsibilities}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </FormField>
                    <DepartmentSelectField
                      name="departmentUuid"
                      error={fieldErrorHandler("departmentUuid")}
                      value={values.departmentUuid}
                      handleBlur={handleBlur}
                      handleChange={handleChange}
                    />
                    <FormField
                      required
                      name="startDate"
                      label={t("Start date")}
                      error={fieldErrorHandler("startDate")}
                    >
                      <SingleDatePicker
                        key={`startDate-${values.startDate}`}
                        placeholder={t("Select date")}
                        id="startDate"
                        name="startDate"
                        value={values.startDate}
                        onChange={startDatePickerHandlePick}
                        onBlur={handleBlur}
                        minDate={minDate}
                        maxDate={maxDate}
                      />
                    </FormField>
                    {getFeatureFlag("release-eor-honored-start-date") && (
                      <FormField
                        name="honoredStartDate"
                        label={t("Honored start date")}
                        error={fieldErrorHandler("honoredStartDate")}
                      >
                        <SingleDatePicker
                          key={`honoredStartDate-${values.honoredStartDate}`}
                          placeholder={t("Select date")}
                          id="honoredStartDate"
                          name="honoredStartDate"
                          value={values.honoredStartDate || ""}
                          onChange={honoredStartDatePickerHandlePick}
                          onBlur={handleBlur}
                          maxDate={farFutureDate}
                        />
                      </FormField>
                    )}
                    <FormField
                      name="payBasis"
                      required
                      label={t("Pay basis")}
                      error={fieldErrorHandler("payBasis")}
                      message={t(
                        "We only support full-time, salaried employees for now."
                      )}
                    >
                      <Select
                        required
                        name="payBasis"
                        placeholder={t("Select pay basis")}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.payBasis}
                      >
                        <SelectOption
                          value={PayBasis.Salary}
                          key={PayBasis.Salary}
                        >
                          {t("Salaried")}
                        </SelectOption>
                        <SelectOption
                          value={PayBasis.Hourly}
                          key={PayBasis.Hourly}
                          disabled
                        >
                          {t("Hourly")}
                        </SelectOption>
                      </Select>
                    </FormField>
                    <FormField
                      required
                      name="payRate"
                      label={payAmountLabel()}
                      error={fieldErrorHandler("payRate")}
                    >
                      <TextInput
                        required
                        name="payRate"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        value={values.payRate.toString()}
                      />
                    </FormField>
                    {values.workCountry &&
                      isCountryExtendedConfig(values.workCountry) && (
                        <div className={styles.cardBody}>
                          {
                            COUNTRY_EXTENDED_CONFIG[values.workCountry]
                              ?.component
                          }
                        </div>
                      )}
                  </div>
                </Card>
              </div>
              <FormField
                name="sendInvitationTo"
                required
                label={t("Send invitation to")}
                error={fieldErrorHandler("sendInvitationTo")}
              >
                <RadioButtonGroup
                  name="sendInvitationTo"
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.sendInvitationTo}
                >
                  <RadioButton
                    value={EmailTypes.Work}
                    label={t("Work email")}
                  />
                  <RadioButton
                    value={EmailTypes.Home}
                    label={t("Home email")}
                    disabled={!values.homeEmail}
                  />
                </RadioButtonGroup>
              </FormField>
            </div>
            <ActionFooter
              actions={[
                <Button key="submit-btn" type="submit" disabled={isLoading}>
                  {t("Submit")}
                </Button>,
              ]}
            />
          </MiloForm>
        );
      }}
    </Formik>
  );
}
